import React, { useState, useEffect, useRef } from 'react';
import GameBoard from '../components/GameBoard';
import { ABILITIES } from '../services/abilityService';
import { ref, update, onValue, get } from 'firebase/database';
import { database } from '../services/firebaseConfig';
import { TbCapsuleHorizontalFilled, TbCapsuleHorizontal } from 'react-icons/tb';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const GameRoom_Desktop = ({
  onNavigateHome,
  isPaused = false,
  isMyTurn = false,
  playerShipsRemaining = 0,
  opponentShipsRemaining = 0,
  playerName = 'You',
  opponentName = 'Opponent',
  playerShipsHit = 0,
  opponentShipsHit = 0,
  playerSuccessfulHits = 0,
  opponentSuccessfulHits = 0,
  onTurnTimeout = () => { },
  onGameTimeout = () => { },
  // Grid and game state props
  playerGrid = [],
  opponentGrid = [],
  gridSize = 8,
  handleAttack = () => { },
  activeAbility = null,
  annihilateVertical = false,
  hackerResult = null,
  // Abilities
  abilities = {},
  onUseAbility = () => { },
  onToggleAnnihilateOrientation = () => { },
  // Additional props needed for notifications
  roomId,
  playerId,
  gameData
}) => {
  const [gameTimeLeft, setGameTimeLeft] = useState(15 * 60);
  const [turnTimeLeft, setTurnTimeLeft] = useState(30);
  const [gameEnded, setGameEnded] = useState(false);

  // Emoji/Message state
  const [messages, setMessages] = useState([]);
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [isCooldown, setIsCooldown] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time continuously to auto-expire messages
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  // Refs for scrollable containers
  const emojiScrollRef = useRef(null);

  // Scroll functions
  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 200;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Send emoji or message
  const sendMessage = async (content, type = 'emoji') => {
    const now = Date.now();

    // Check cooldown (4 seconds)
    if (now - lastMessageTime < 4000) {
      console.log('Cooldown active, please wait');
      return;
    }

    if (!roomId || !playerId) return;

    try {
      const messageData = {
        content,
        type, // 'emoji' or 'message'
        senderId: playerId,
        senderName: playerName,
        timestamp: now
      };

      // Push to Firebase
      await update(ref(database, `rooms/${roomId}/messages/${now}`), messageData);

      setLastMessageTime(now);
      setIsCooldown(true);

      // Remove cooldown after 4 seconds
      setTimeout(() => setIsCooldown(false), 4000);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Listen for messages
  useEffect(() => {
    if (!roomId) return;

    const messagesRef = ref(database, `rooms/${roomId}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.values(data)
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 10); // Keep last 10 messages
        setMessages(messageList);
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  // [Timer logic - keeping all the existing useEffect hooks]
  useEffect(() => {
    if (!roomId) return;

    const gameTimerRef = ref(database, `rooms/${roomId}/gameTimer`);
    onValue(gameTimerRef, (snapshot) => {
      if (!snapshot.exists() && !gameData?.gameOver) {
        update(ref(database, `rooms/${roomId}`), {
          gameTimer: {
            timeRemaining: 15 * 60,
            isPaused: isPaused || false
          }
        });
      }
    }, { onlyOnce: true });
  }, [roomId, gameData?.gameOver, isPaused]);

  useEffect(() => {
    if (!roomId) return;

    const gameTimerRef = ref(database, `rooms/${roomId}/gameTimer`);
    const unsubscribe = onValue(gameTimerRef, (snapshot) => {
      const timerData = snapshot.val();
      if (timerData) {
        setGameTimeLeft(timerData.timeRemaining || 15 * 60);

        if (timerData.timeRemaining <= 0 && !gameData?.gameOver && !gameEnded) {
          setGameEnded(true);
          let winner = null;

          if (playerShipsHit > opponentShipsHit) {
            winner = 'player';
          } else if (opponentShipsHit > playerShipsHit) {
            winner = 'opponent';
          } else {
            if (playerSuccessfulHits > opponentSuccessfulHits) {
              winner = 'player';
            } else if (opponentSuccessfulHits > playerSuccessfulHits) {
              winner = 'opponent';
            } else {
              winner = 'draw';
            }
          }

          onGameTimeout(winner);
        }
      }
    });

    return () => unsubscribe();
  }, [roomId, gameData?.gameOver, onGameTimeout, gameEnded, playerShipsHit, opponentShipsHit, playerSuccessfulHits, opponentSuccessfulHits]);

  useEffect(() => {
    if (gameData?.gameOver || !roomId || isPaused || gameEnded) return;

    const players = gameData?.players ? Object.keys(gameData.players) : [];
    const isPlayer1 = players.length > 0 && playerId === players[0];

    if (!isPlayer1) return;

    const timerInterval = setInterval(() => {
      get(ref(database, `rooms/${roomId}/gameTimer`)).then((snapshot) => {
        const timerData = snapshot.val();
        if (timerData && timerData.timeRemaining > 0 && !timerData.isPaused) {
          const newTimeRemaining = timerData.timeRemaining - 1;
          update(ref(database, `rooms/${roomId}/gameTimer`), {
            timeRemaining: newTimeRemaining,
            isPaused: isPaused || false
          });
        }
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [gameData?.gameOver, gameData?.players, isPaused, onGameTimeout, roomId, playerId, gameEnded]);

  useEffect(() => {
    if (!roomId || gameData?.gameOver || isPaused === undefined) return;

    update(ref(database, `rooms/${roomId}/gameTimer`), {
      isPaused: isPaused
    });
  }, [isPaused, roomId, gameData?.gameOver]);

  useEffect(() => {
    if (!roomId) return;

    const turnTimerRef = ref(database, `rooms/${roomId}/turnTimer`);
    const unsubscribe = onValue(turnTimerRef, (snapshot) => {
      const turnData = snapshot.val();
      if (turnData) {
        setTurnTimeLeft(turnData.timeRemaining || 30);

        if (turnData.timeRemaining <= 0 && !gameData?.gameOver) {
          const currentPlayerId = localStorage.getItem('battleshipPlayerId');
          if (gameData?.currentTurn === currentPlayerId) {
            onTurnTimeout();
          }
        }
      }
    });

    return () => unsubscribe();
  }, [roomId, isMyTurn, gameData?.gameOver, gameData?.currentTurn, onTurnTimeout]);

  useEffect(() => {
    if (!roomId || gameData?.gameOver) return;

    const timeLimit = gameData?.settings?.turnTimeLimit || 30;

    update(ref(database, `rooms/${roomId}/turnTimer`), {
      timeRemaining: timeLimit,
      isPaused: isPaused || false,
      currentPlayer: isMyTurn ? playerId : null
    });

    setTurnTimeLeft(timeLimit);
  }, [isMyTurn, gameData?.settings?.turnTimeLimit, roomId, gameData?.gameOver, isPaused, playerId]);

  useEffect(() => {
    if (!isMyTurn || isPaused || gameData?.gameOver || !roomId) return;

    const turnInterval = setInterval(() => {
      get(ref(database, `rooms/${roomId}/turnTimer`)).then((snapshot) => {
        const turnData = snapshot.val();
        if (turnData && turnData.timeRemaining > 0 && !turnData.isPaused) {
          const newTimeRemaining = turnData.timeRemaining - 1;
          update(ref(database, `rooms/${roomId}/turnTimer`), {
            timeRemaining: newTimeRemaining,
            isPaused: isPaused || false
          });

          if (newTimeRemaining <= 0) {
            const currentPlayerId = localStorage.getItem('battleshipPlayerId');
            if (gameData?.currentTurn === currentPlayerId) {
              onTurnTimeout();
            }
          }
        }
      });
    }, 1000);

    return () => clearInterval(turnInterval);
  }, [isMyTurn, isPaused, gameData?.gameOver, gameData?.currentTurn, onTurnTimeout, roomId]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const turnProgress = ((30 - turnTimeLeft) / 30) * 100;

  const handleSurrender = async () => {
    if (window.confirm('Are you sure you want to surrender?')) {
      try {
        if (roomId && playerId && gameData) {
          await update(ref(database, `rooms/${roomId}`), {
            gameOver: true,
            winner: Object.keys(gameData.players).find(id => id !== playerId),
            endReason: 'surrender',
            surrenderedBy: playerId,
            endTime: Date.now(),
            notifications: {
              [`surrender_${Date.now()}`]: {
                type: 'surrender',
                playerName: playerName,
                message: `${playerName} has surrendered the game`,
                timestamp: Date.now()
              }
            }
          });
        }
        onNavigateHome?.();
      } catch (error) {
        console.error('Error surrendering:', error);
        onNavigateHome?.();
      }
    }
  };

  const handleLeave = async () => {
    if (window.confirm('Are you sure you want to leave?')) {
      try {
        if (roomId && playerId && gameData) {
          await update(ref(database, `rooms/${roomId}`), {
            gameOver: true,
            winner: Object.keys(gameData.players).find(id => id !== playerId),
            endReason: 'leave',
            leftBy: playerId,
            endTime: Date.now(),
            notifications: {
              [`leave_${Date.now()}`]: {
                type: 'leave',
                playerName: playerName,
                message: `${playerName} has left the game`,
                timestamp: Date.now()
              }
            }
          });
        }
        onNavigateHome?.();
      } catch (error) {
        console.error('Error leaving:', error);
        onNavigateHome?.();
      }
    }
  };

  // Convert abilities object to array
  const abilityList = Object.entries(abilities || {})
    .filter(([, data]) => data.active)
    .map(([key, data]) => ({
      key,
      name: ABILITIES[key]?.name || key,
      icon: ABILITIES[key]?.icon || '⚡',
      count: data.count || 1,
      used: data.used || false,
      ...ABILITIES[key]
    }));

  return (
    <div className="h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white flex flex-col gap-4 p-4">
      {/* Hidden scrollbar styles */}
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.5) translateY(20px); }
          50% { transform: scale(1.2) translateY(-10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bounceSubtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-pop-in {
          animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        .animate-bounce-subtle {
          animation: bounceSubtle 2s infinite ease-in-out;
        }
      `}</style>

      {/* TOP HEADER - Game Info Bar */}
      <div className="h-20 border border-gray-600/50 rounded-xl flex items-center justify-between px-6 bg-gray-800/50 backdrop-blur-md">
        {/* Surrender Button */}
        <button
          onClick={handleSurrender}
          className="px-6 py-3 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 rounded-lg transition-all"
        >
          <span className="text-red-400 font-semibold">Surrender</span>
        </button>

        {/* Game Timer */}
        <div className="flex flex-col items-center">
          <div className="bg-gray-900 px-4 py-2 rounded-lg border border-gray-600">
            <span className="text-xl font-mono font-bold text-gray-200">{formatTime(gameTimeLeft)}</span>
          </div>
        </div>

        {/* Turn Indicator */}
        <div className="flex flex-col items-center">
          <div className={`px-6 py-2 rounded-lg font-bold text-lg shadow-lg ${isMyTurn ? 'bg-green-600' : 'bg-yellow-600'
            }`}>
            {isMyTurn ? 'YOUR TURN' : `${opponentName}'S TURN`}
          </div>
        </div>

        {/* Turn Timer */}
        <div className="flex flex-col items-center">
          <div className="relative w-16 h-16">
            <svg className="transform -rotate-90 w-16 h-16" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="28" stroke="#374151" strokeWidth="4" fill="none" />
              <circle cx="32" cy="32" r="28" stroke="#10b981" strokeWidth="4" fill="none"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (turnProgress / 100)}`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold text-green-400">{turnTimeLeft}s</span>
            </div>
          </div>
        </div>

        {/* Ships Status - Capsule Visualization */}
        <div className="flex flex-col gap-2">
          {/* Player Ships - Green Row */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-16">You:</span>
            <div className="flex gap-1">
              {[...Array(playerShipsRemaining)].map((_, i) => (
                <TbCapsuleHorizontalFilled key={`player-filled-${i}`} className="text-green-500 text-xl" />
              ))}
              {[...Array(5 - playerShipsRemaining)].map((_, i) => (
                <TbCapsuleHorizontal key={`player-empty-${i}`} className="text-gray-600 text-xl" />
              ))}
            </div>
          </div>

          {/* Enemy Ships - Red Row */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-16">Enemy:</span>
            <div className="flex gap-1">
              {[...Array(opponentShipsRemaining)].map((_, i) => (
                <TbCapsuleHorizontalFilled key={`enemy-filled-${i}`} className="text-red-500 text-xl" />
              ))}
              {[...Array(5 - opponentShipsRemaining)].map((_, i) => (
                <TbCapsuleHorizontal key={`enemy-empty-${i}`} className="text-gray-600 text-xl" />
              ))}
            </div>
          </div>
        </div>

        {/* Leave Button */}
        <button
          onClick={handleLeave}
          className="px-6 py-3 bg-gray-600/20 hover:bg-gray-600/40 border border-gray-500/50 rounded-lg transition-all"
        >
          <span className="text-gray-400 font-semibold">Leave</span>
        </button>
      </div>

      {/* MAIN CONTENT AREA - Full Height */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* LEFT SIDE - Enemy Grid - Full Height */}
        <div className="flex-1 max-w-[65%] border-2 border-gray-600 rounded-xl p-6 bg-gray-800/30 backdrop-blur-sm min-w-0 flex items-center justify-center relative">
          <div className="h-full aspect-square relative">
            <GameBoard
              grid={opponentGrid}
              gridSize={gridSize}
              isPlayerGrid={false}
              onCellClick={handleAttack}
              activeAbility={activeAbility}
              annihilateVertical={annihilateVertical}
              hackerResult={hackerResult}
            />

            {/* Message Overlay - Shows messages < 2 seconds old */}
            <div className="absolute bottom-10 left-0 right-0 flex flex-col-reverse items-center gap-4 pointer-events-none z-30">
              {messages
                .filter(msg => currentTime - msg.timestamp < 2000)
                .slice(0, 5) // Show up to 5 overlapping messages
                .map((msg, i) => (
                  <div
                    key={`${msg.timestamp}-${i}`}
                    className="animate-pop-in text-white flex items-center justify-center transform transition-all"
                    style={{
                      textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.5)'
                    }}
                  >
                    <span className="text-5xl font-bold animate-bounce-subtle">{msg.content}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - My Grid + Messages + Abilities */}
        <div className="flex-[1] flex flex-col gap-4 min-w-0">
          {/* My Grid */}
          <div className="flex-[2] border-2 border-blue-500/50 rounded-xl p-4 bg-gray-800/30 backdrop-blur-sm min-h-0 flex items-center justify-center">
            <div className="h-full aspect-square">
              <GameBoard
                grid={playerGrid}
                gridSize={gridSize}
                isPlayerGrid={true}
              />
            </div>
          </div>

          {/* Emojis/Messages - Minimalist Design */}
          <div className="flex-[1] border border-gray-600/50 rounded-lg p-3 bg-gray-800/20 backdrop-blur-sm overflow-hidden">
            <div className="h-full flex flex-col gap-2">
              {/* Combined Row - Emojis + Messages */}
              <div className="relative group">
                {/* Cooldown indicator */}
                {isCooldown && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 z-30">
                    <div className="h-full bg-blue-400 animate-shrink" style={{ animation: 'shrink 4s linear' }}></div>
                  </div>
                )}

                {/* Left chevron */}
                <button
                  onClick={() => scroll(emojiScrollRef, 'left')}
                  className="absolute -left-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 bg-gray-700/80 hover:bg-gray-600 rounded-full border border-gray-500/50 flex items-center justify-center transition-all shadow-lg opacity-0 group-hover:opacity-100"
                >
                  <FaChevronLeft className="text-gray-300 text-xs" />
                </button>

                <div
                  ref={emojiScrollRef}
                  className="flex gap-2 overflow-x-scroll hide-scrollbar"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {/* Emojis */}
                  {['😀', '😎', '👍', '🎯', '⚡', '🔥'].map((emoji, i) => (
                    <button
                      key={`emoji-${i}`}
                      onClick={() => sendMessage(emoji, 'emoji')}
                      disabled={isCooldown}
                      className={`shrink-0 w-10 h-10 bg-gray-700/40 hover:bg-gray-600/60 rounded-lg border border-gray-600/30 flex items-center justify-center transition-all text-lg ${isCooldown ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
                        }`}
                    >
                      {emoji}
                    </button>
                  ))}

                  {/* Divider */}
                  <div className="shrink-0 w-px h-10 bg-gray-600/50 mx-1"></div>

                  {/* Messages */}
                  {['GG', 'Nice!', 'Ouch', 'GLHF', 'WP'].map((msg, i) => (
                    <button
                      key={`msg-${i}`}
                      onClick={() => sendMessage(msg, 'message')}
                      disabled={isCooldown}
                      className={`shrink-0 px-3 h-10 bg-gray-700/40 hover:bg-blue-600/60 rounded-lg border border-gray-600/30 hover:border-blue-500/40 flex items-center justify-center transition-all text-xs font-medium whitespace-nowrap ${isCooldown ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                        }`}
                    >
                      {msg}
                    </button>
                  ))}
                </div>

                {/* Right chevron */}
                <button
                  onClick={() => scroll(emojiScrollRef, 'right')}
                  className="absolute -right-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 bg-gray-700/80 hover:bg-gray-600 rounded-full border border-gray-500/50 flex items-center justify-center transition-all shadow-lg opacity-0 group-hover:opacity-100"
                >
                  <FaChevronRight className="text-gray-300 text-xs" />
                </button>
              </div>

              {/* Message History - Real Messages from Firebase */}
              <div className="flex-1 bg-gray-900/40 rounded-lg p-2 border border-gray-700/30 overflow-y-auto min-h-0">
                <div className="space-y-1.5">
                  {messages.length === 0 ? (
                    <div className="text-xs text-gray-500 text-center">No messages yet</div>
                  ) : (
                    messages.map((message, index) => (
                      <div key={index} className="text-xs text-gray-400 animate-fadeIn">
                        <span className={`font-medium ${message.senderId === playerId ? 'text-blue-400' : 'text-red-400'
                          }`}>
                          {message.senderId === playerId ? 'You' : message.senderName}:
                        </span>{' '}
                        {message.content}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Abilities Panel - Square Cards with Horizontal Scroll + Config */}
          <div className="flex-[1.5] border border-gray-600/50 rounded-lg bg-gray-800/20 backdrop-blur-sm overflow-hidden flex gap-2 p-2">
            {/* Ability Cards - 90% - Horizontal Scroll */}
            <div
              className="w-[90%] h-full overflow-x-scroll overflow-y-hidden hide-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div className="flex gap-2 h-full items-center min-w-max">
                {abilityList.map((ability) => (
                  <button
                    key={ability.key}
                    onClick={() => {
                      if (!isMyTurn) return;
                      if (activeAbility === ability.key) {
                        onUseAbility(null);
                      } else if (!ability.used) {
                        onUseAbility(ability.key);
                      }
                    }}
                    disabled={!isMyTurn || ability.used}
                    className={`shrink-0 h-full aspect-square rounded-lg border-2 flex flex-col items-center justify-center transition-all relative ${activeAbility === ability.key
                      ? 'bg-blue-600/50 border-blue-400/70 scale-105'
                      : !ability.used && isMyTurn
                        ? 'bg-gray-800/50 border-gray-600/50 hover:bg-blue-600/30 hover:border-blue-500/50 cursor-pointer'
                        : 'bg-gray-700/30 border-gray-700/50 opacity-50 cursor-not-allowed'
                      }`}
                  >
                    <span className="text-3xl mb-1">{ability.icon}</span>
                    <span className="text-[10px] text-gray-400 font-medium">{ability.name}</span>

                    {/* Count Badge */}
                    {!ability.used && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-[10px] font-bold border border-gray-900">
                        {ability.count}
                      </div>
                    )}

                    {/* Active indicator */}
                    {activeAbility === ability.key && (
                      <div className="absolute inset-0 rounded-lg border-2 border-blue-400 animate-pulse"></div>
                    )}
                  </button>
                ))}

                {/* Empty slots */}
                {abilityList.length < 3 && [...Array(3 - abilityList.length)].map((_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="shrink-0 h-full aspect-square rounded-lg border-2 border-dashed border-gray-600/50 bg-gray-800/20 flex flex-col items-center justify-center"
                  >
                    <span className="text-2xl mb-1">🔒</span>
                    <span className="text-[10px] text-gray-500 font-medium">Empty</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Config Section - 10% - Rotation Control */}
            <div className="w-[10%] h-full bg-gray-800/50 border border-gray-600/50 rounded-lg flex flex-col items-center justify-center gap-1 p-1">
              <span className="text-[9px] text-gray-500 uppercase font-semibold">Rotate</span>
              <button
                onClick={onToggleAnnihilateOrientation}
                disabled={activeAbility !== 'ANNIHILATE'}
                className={`w-full aspect-square border rounded flex items-center justify-center transition-all ${activeAbility === 'ANNIHILATE'
                  ? 'bg-gray-700/50 hover:bg-blue-600/30 border-gray-600/50 hover:border-blue-500/50 hover:scale-110 cursor-pointer'
                  : 'bg-gray-800/30 border-gray-700/30 opacity-50 cursor-not-allowed'
                  }`}
              >
                <span className={`text-xl transform transition-transform ${annihilateVertical ? 'rotate-90' : ''}`}>↻</span>
              </button>
              {activeAbility === 'ANNIHILATE' && (
                <div className="text-[8px] text-gray-400 text-center">
                  {annihilateVertical ? 'Vertical' : 'Horizontal'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameRoom_Desktop;
